import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { HiDotsVertical, HiOutlinePencil, HiOutlineTrash, HiOutlineMail } from 'react-icons/hi';
import { Toaster, toast} from 'react-hot-toast';
import { find, includes, sortBy } from 'lodash';

import { useUsersActions } from '../../../services/actions/users.actions';
import { crudUserAtom, usersAtom } from '../../../services/atoms/users.atoms';
import { SearchBanner } from "../../components/search/search-banner";
import InviteUserModal from './components/create-modal';
import LocalTable from '../../../components/table-local';
import EditUserModal from './components/edit-modal';
import ConfirmDialog from '../../../components/confirm-dialog/confirm-dialog';

const EDIT_OPTION = { id: 'opt-1', title: 'Editar Usuario', action: 'edit', icon: HiOutlinePencil };
const REMOVE_OPTION = { id: 'opt-2', title: 'Eliminar Usuario', action: 'remove', icon: HiOutlineTrash };
const RESEND_OPTION = { id: 'opt-3', title: 'Reenviar invitación', action: 'resend', icon: HiOutlineMail };

// Quien ya activó su cuenta no necesita invitación.
const optionsFor = (row) => (row?.status === 'active' ? [EDIT_OPTION, REMOVE_OPTION] : [RESEND_OPTION, EDIT_OPTION, REMOVE_OPTION]);

const STATUS = {
  active: { label: 'Activo', color: '#1e7e34', background: '#e3f4e8' },
  invitation_pending: { label: 'Invitación pendiente', color: '#8a5a00', background: '#fff3d6' },
  invitation_expired: { label: 'Invitación vencida', color: '#b02a37', background: '#fbe3e5' },
  password_change_required: { label: 'Debe cambiar contraseña', color: '#0f3e4e', background: '#e3eef2' },
  disabled: { label: 'Deshabilitado', color: '#6c757d', background: '#eceeef' },
};

const roleLabel = (user) => (user.isSuperAdmin ? 'Super admin' : user.isAdmin ? 'Admin cliente' : 'Usuario cliente');

const getAttribute = (Attributes, key) => find(Attributes, (e) => e.Name === key)?.Value || '';

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.active;
  return (
    <span style={{ color: s.color, background: s.background, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
};

const columns = [
  { Header: 'Nombre Completo', accessor: ({ Attributes }) => <div>{getAttribute(Attributes, 'name')}</div>, disableSortBy: true },
  { Header: 'Correo', accessor: ({ Attributes }) => <div>{getAttribute(Attributes, 'email')}</div>, disableSortBy: true },
  { Header: 'Rol', accessor: (user) => <div>{roleLabel(user)}</div>, disableSortBy: true },
  { Header: 'Empresa', accessor: (user) => <div>{user.isSuperAdmin ? 'GrowMe' : user.clientName}</div>, disableSortBy: true },
  { Header: 'Estado', accessor: (user) => <StatusBadge status={user.status} />, disableSortBy: true },
  { Header: 'Acciones', accessor: '', type: 'actions', disableSortBy: true },
];

const Users = () => {
  const { disableUser, userList, resendInvitation } = useUsersActions();
  const [users] = useRecoilState(usersAtom);
  const [open, setOpen] = useState({ edit: false, create: false });
  const [selectedUser, setUserSelected] = useState(null);
  const [search, setSearch] = useState('');
  // Usuario que se está por eliminar (null = diálogo cerrado).
  const [pendingDelete, setPendingDelete] = useState(null);
  const userResolver = useRecoilValue(crudUserAtom);

  useEffect(() => {
    // Load users when component mounts
    userList();
    // eslint-disable-next-line
  }, []);

  // Super admins primero, después por empresa y nombre.
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = (users?.payload || []).filter((user) => {
      if (!term) return true;
      return [getAttribute(user.Attributes, 'name'), getAttribute(user.Attributes, 'email'), user.clientName]
        .some((value) => (value || '').toLowerCase().includes(term));
    });
    return sortBy(filtered, [
      (user) => (user.isSuperAdmin ? 0 : 1),
      (user) => (user.clientName || '').toLowerCase(),
      (user) => getAttribute(user.Attributes, 'name').toLowerCase(),
    ]);
  }, [users?.payload, search]);

  const handleOpenModal = (key) => setOpen(modal => ({ ...modal, [key]: !modal[key] }));

  const handleOptionAction = (action, row) => {
    if (action === 'remove') {
      handleDisableUser(row);
    };
    if (action === 'edit') {
      setUserSelected(row);
      handleOpenModal('edit');
    };
    if (action === 'resend') {
      handleResend(row);
    };
  };

  const handleResend = async (row) => {
    const email = getAttribute(row?.Attributes, 'email');
    const name = (getAttribute(row?.Attributes, 'name') || '').split(' ')[0];
    try {
      await resendInvitation(email, name);
      toast.success(`Invitación reenviada a ${email}`, { duration: 4000 });
      userList();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'No se pudo reenviar la invitación', { duration: 4000 });
    }
  };

  // Mismo flujo de antes: si falla, se avisa y el diálogo queda abierto para reintentar.
  const handleConfirmDelete = async () => {
    const row = pendingDelete;
    if (!row) return;
    let username = find(row?.Attributes || [], (e) => e?.Name === 'email');

    await disableUser(username?.Value || '', (res) => {
      if (includes(res?.message, 'Request failed with status')) {
        toast.error('El usuario no se ha podido eliminar', {duration:4000});
      } else {
        toast.success('Usuario eliminado exitosamente', {duration:4000});
        userList();
        setPendingDelete(null);
      }
    });
  };

  const handleDisableUser = (row) => setPendingDelete(row);

  const actions = [
    { icon: HiDotsVertical, dropdown: true },
  ];

  return (
    <div className="gm-users-screen-container">
      <Toaster />
      {open.create && <InviteUserModal
        open={open.create}
        toggle={() => handleOpenModal('create')}
        onInvited={() => userList()}
      />}
      {open.edit && <EditUserModal
        open={open.edit}
        toggle={() => handleOpenModal('edit')}
        selectedUser={selectedUser}
      />}
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="¿Eliminar este usuario?"
        description={`${find(pendingDelete?.Attributes || [], (e) => e?.Name === 'email')?.Value || 'El usuario'} ya no podrá entrar a GrowMe.`}
        confirmLabel="Eliminar"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
      <h2>Usuarios GrowMe</h2>
      <SearchBanner
        placeholder="Buscar por nombre, correo o empresa"
        buttonText="Invitar usuario"
        showSearch={true}
        value={search}
        search={search}
        onChange={(e) => setSearch(e.target.value)}
        onSubmitSearch={(e) => e.preventDefault()}
        onClickClean={() => setSearch('')}
        onClickAction={() => handleOpenModal('create')}
      />
      <LocalTable
        columns={columns}
        actions={actions}
        options={optionsFor}
        handleOptionAction={handleOptionAction}
        data={rows}
        loading={users.loading || userResolver.loading}
      />
    </div>
  );
}

export default Users;
